<template>
  <v-container class="crop-editor px-0 py-0">
    <v-container class="crop-editor-content">
      <v-row justify="center" class="custom-scroll-bar crop-editor-row">
        <v-col cols="12" sm="3" style="text-align: -webkit-center">
            <v-img max-height="124" max-width="124" :src="require('@/assets/min-lot.png')"/>
        </v-col>
        <v-col cols="12" sm="9" class="text-left">
          <v-form v-model="isFormValid" ref="cropForm">
            <v-row no-gutters>
              <v-col cols="12" sm="6" class="my-3 px-2">
                <v-autocomplete
                  :menu-props="{ bottom: true, offsetY: true }"
                  :items="devices"
                  item-text="campaign.entityName"
                  item-value="id"
                  multiple
                  v-model="newCropData.devices"
                  :label="$t('AddCrop.cropDevices')"
                  outlined
                  background-color="#FFF"
                  color="#0098E1"
                  required
                  :no-data-text="$t('AddCrop.noDevices')"
                  hide-details="auto"
                >
                  <template v-slot:item="{ active, item, attrs, on }">
                    <v-list-item v-on="on" v-bind="attrs" #default="{ active }">
                      <v-list-item-action>
                      <v-checkbox :input-value="active"/>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          <v-row no-gutters align="center">
                          <span>{{ item.campaign.entityName }} - {{ getEquipmentTypeLabel(item.equipment.type) }}</span>
                          </v-row>
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="12" sm="6" class="my-3 px-2">
                <v-text-field
                  :label="`${$t('AddCrop.cropName')} *`"
                  :title="`${$t('AddCrop.cropName')} *`"
                  v-model="newCropData.name"
                  outlined
                  background-color="#FFF"
                  color="#0098E1"
                  :rules="[rules.fieldRequired]"
                  required
                  maxlength="30"
                  hide-details="auto"
                />
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="12" sm="6" class="my-3 px-2">
                <v-select
                  :menu-props="{ bottom: true, offsetY: true }"
                  :items="cropTypes"
                  v-model="newCropData.type"
                  :label="`${$t('AddCrop.cropType')} *`"
                  :title="`${$t('AddCrop.cropType')} *`"
                  outlined
                  background-color="#FFF"
                  color="#0098E1"
                  :rules="[rules.fieldRequired, rules.arrayFieldValidation]"
                  required
                  multiple
                  clearable
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" sm="6" v-if="!isNewCrop" class="my-3 px-2">
                <v-select
                  :menu-props="{ bottom: true, offsetY: true }"
                  :items="fields"
                  item-text="name"
                  item-value="id"
                  v-model="newCropData.field"
                  :label="`${$t('AddCrop.cropField')} *`"
                  :title="`${$t('AddCrop.cropField')} *`"
                  outlined
                  background-color="#FFF"
                  color="#0098E1"
                  :rules="[rules.fieldRequired]"
                  required
                  hide-details="auto"
                >
                  <template v-slot:append-item>
                    <v-list-item @click="addNewField">
                      <v-list-item-title class="add-field-btn">
                        {{ $t('AddCrop.addNewField') }}
                      </v-list-item-title>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="12" sm="6" class="my-3 px-2">
                <v-menu
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="newCropData.sowingDate"
                      :label="`${$t('AddCrop.cropSowingDate')}`"
                      :title="`${$t('AddCrop.cropSowingDate')}`"
                      append-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                      background-color="#FFF"
                      color="#0098E1"
                      clearable
                      hide-details="auto"
                    />
                  </template>
                  <v-date-picker
                    v-model="auxDate"
                    color="#0098E1"
                    locale="es-AR"
                    @input="dateMenu = false"
                  />
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6" class="my-3 px-2">
                <v-text-field
                  :label="`${$t('AddCrop.cropArea')} *`"
                  :title="`${$t('AddCrop.cropArea')} *`"
                  v-model.number="newCropData.area"
                  outlined
                  background-color="#FFF"
                  color="#0098E1"
                  :rules="[rules.fieldRequired, rules.numberValidation]"
                  required
                  hide-details="auto"
                />
              </v-col>
            </v-row>
            <v-row no-gutters v-if="newCropData.shapeType === 3" justify="center">
              <v-col cols="8" sm="4" class="px-2">
                <v-btn
                  @click="toggleMap"
                  elevation="0"
                  color="#0098E1"
                  class="toggle-btn"
                  block
                >
                  {{cropMapSelection ? $t('AddCrop.hideMap') : $t('AddCrop.showMap')}}
                </v-btn>
              </v-col>
              <v-col cols="8" sm="4" class="px-2">
                <v-btn
                  class="toggle-btn"
                  @click="toggleCoordinates"
                  elevation="0"
                  color="#0098E1"
                  block
                >
                  {{showCoordinates ? $t('AddCrop.hideCoordinates') : $t('AddCrop.showCoordinates')}}
                </v-btn>
              </v-col>
            </v-row>
            <v-row v-if="newCropData.shapeType === 3" v-show="cropMapSelection" no-gutters style="height: 400px" class="px-2 my-3">
              <CropMap :cropMapSelection.sync="cropMapSelection" :cropData.sync="newCropData"/>
            </v-row>
            <v-row no-gutters>
              <v-col cols="12">
                <div v-if="newCropData.shapeType === 1">
                  <v-row no-gutters>
                    <v-col cols="12" sm="6" class="my-3 px-2">
                      <v-text-field
                        :label="`${$t('AddCrop.centerCoordinates')} *`"
                        :title="`${$t('AddCrop.centerCoordinates')} *`"
                        v-model="newCropData.centerCoordinatesString"
                        validate-on-blur
                        outlined
                        :hint="`${$t('AddCrop.coordinatesHint')}`"
                        persistent-hint
                        background-color="#FFF"
                        color="#0098E1"
                        required
                        :rules="[rules.fieldRequired, rules.validateLatLon]"
                        hide-details="auto"
                        @blur="handleCenterCoordinatesBlur"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" class="my-3 px-2">
                      <v-text-field
                        :label="`${$t('AddDevice.radius')} *`"
                        :title="`${$t('AddDevice.radius')} *`"
                        v-model.number="newCropData.radius"
                        outlined
                        background-color="#FFF"
                        color="#0098E1"
                        required
                        :rules="[rules.fieldRequired, rules.numberValidation]"
                        hide-details="auto"
                      />
                    </v-col>
                  </v-row>
                  <v-row no-gutters>
                    <v-col cols="12" sm="7" class="my-3 px-2">
                      <v-row>
                        <a @click="startAndEndPointsFlag = !startAndEndPointsFlag" class="advanced-cfg float-left">{{ startAndEndPointsFlag ? '-' : '+' }} {{ $t('AddDevice.startAndEndPoints') }}</a>
                      </v-row>
                      <template v-if="startAndEndPointsFlag">
                        <v-row class="mt-3">
                          <v-col cols="8" class="px-0 py-0">
                            <v-text-field
                              :label="`${$t('AddDevice.startCoordinates')} *`"
                              :title="`${$t('AddDevice.startCoordinates')} *`"
                              v-model="newCropData.startCoordinatesString"
                              validate-on-blur
                              outlined
                              background-color="#FFF"
                              color="#0098E1"
                              required
                              hide-details="auto"
                              :rules="[rules.fieldRequired, rules.validateLatLon]"
                              @blur="handleCropCoordinatesBlur(newCropData.startCoordinatesString, 'start')"
                            />
                          </v-col>
                          <v-col v-if="newCropData.startAngle !== undefined" cols="4" class="pl-2 pr-0 angle-hint">
                            {{this.$t('AddCrop.angleHint', {angle: newCropData.startAngle.toFixed(0)})}}
                          </v-col>
                        </v-row>
                        <v-checkbox
                          class="my-5"
                          v-model="endPointEqualToStart"
                          :label="$t('AddDevice.endPointEqualToStart')"
                          hide-details
                        />
                        <v-row v-if="!endPointEqualToStart">
                          <v-col cols="8" class="px-0 py-0">
                            <v-text-field
                              cols="12" sm="6"
                              :label="`${$t('AddDevice.endCoordinates')} *`"
                              :title="`${$t('AddDevice.endCoordinates')} *`"
                              v-model="newCropData.endCoordinatesString"
                              validate-on-blur
                              outlined
                              background-color="#FFF"
                              color="#0098E1"
                              required
                              hide-details="auto"
                              :rules="[rules.fieldRequired, rules.validateLatLon]"
                              @blur="handleCropCoordinatesBlur(newCropData.endCoordinatesString, 'end')"
                            />
                          </v-col>
                          <v-col v-if="newCropData.endAngle !== undefined" cols="4" class="pl-2 pr-0 angle-hint">
                            {{this.$t('AddCrop.angleHint', {angle: newCropData.endAngle !== newCropData.startAngle || newCropData.startAngle !== 0 ? newCropData.endAngle.toFixed(0) : 360})}}
                          </v-col>
                        </v-row>
                      </template>
                    </v-col>
                    <v-col cols="12" sm="5" class="px-5 my-5">
                      <PivotCropArea :cropData="newCropData"/>
                    </v-col>
                  </v-row>
                </div>
                <div v-else-if="newCropData.shapeType === 2">
                  <v-row>
                      <v-col cols="12" sm="6" class="my-3 px-2 py-0">
                        <v-text-field
                          :label="`${$t('AddDevice.startCoordinates')} *`"
                          :title="`${$t('AddDevice.startCoordinates')} *`"
                          v-model="newCropData.startCoordinatesString"
                          validate-on-blur
                          outlined
                          class="mb-2"
                          :hint="`${$t('AddDevice.coordinatesHint')}`"
                          persistent-hint
                          background-color="#FFF"
                          color="#0098E1"
                          required
                          :rules="[rules.fieldRequired, rules.validateLatLon]"
                          hide-details="auto"
                          @blur="handleCropCoordinatesBlur(newCropData.startCoordinatesString, 'start')"
                        />
                      </v-col>
                      <v-col cols="12" sm="6"  class="my-3 px-2 py-0">
                        <v-text-field
                          class='mb-2'
                          :label="`${$t('AddDevice.endCoordinates')} *`"
                          :title="`${$t('AddDevice.endCoordinates')} *`"
                          v-model="newCropData.endCoordinatesString"
                          validate-on-blur
                          outlined
                          :hint="`${$t('AddDevice.coordinatesHint')}`"
                          persistent-hint
                          background-color="#FFF"
                          color="#0098E1"
                          required
                          :rules="[rules.fieldRequired, rules.validateLatLon]"
                          hide-details="auto"
                          @blur="handleCropCoordinatesBlur(newCropData.endCoordinatesString, 'end')"
                        />
                      </v-col>
                  </v-row>
                </div>
                <div v-else-if="newCropData.shapeType === 3 && showCoordinates">
                  <v-row v-for="(point, pointIndex) in cropIRPoints" :key="`point_${pointIndex}`" justify="center">
                    <v-col cols="9" class="pb-0">
                      <v-text-field
                        :label="`${$t('AddDevice.coordinates')} *`"
                        :title="`${$t('AddDevice.coordinates')} *`"
                        v-model="point.coordinatesString"
                        validate-on-blur
                        outlined
                        background-color="#FFF"
                        color="#0098E1"
                        :hint="`${$t('FieldEditor.coordinatesHint')}`"
                        persistent-hint
                        required
                        :rules="[rules.fieldRequired, rules.validateLatLon]"
                        hide-details="auto"
                        class="mb-2"
                        @blur="handleIRCropCoordinatesBlur(point.coordinatesString, pointIndex)"
                      >
                        <template v-slot:prepend>
                          <p class="mb-0 mr-1 text-end">
                            {{ getPointLetter(pointIndex) }}
                          </p>
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col cols="1" class="pb-0">
                      <v-btn fab small elevation="1" color="#FFF" @click="deleteIRPoint(pointIndex)">
                        <v-icon color="black">remove</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                  <v-row align="center" class="justify-start">
                    <v-btn fab x-small elevation="1" color="#FFF" @click="addIRPoint">
                      <v-icon color="#0098E1">add</v-icon>
                    </v-btn>
                    <span class="pl-2">{{$t('AddDevice.addPoint')}}</span>
                  </v-row>
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-col>
      </v-row>
    </v-container>
    <v-row class="mt-3 crop-editor-button-row">
      <v-col cols="12" sm="4" offset-sm="4" class="align-center">
        <v-btn
          @click="saveHandler"
          block
          color="#0098E1"
          large
          class="form-confirmation-button"
          dark
          :loading="loading"
        >{{$t('AddDevice.register')}}</v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { firestore } from '../../plugins/firebase'
import { createCrop, updateCrop } from '../../lib/crudOperations/write'
import CropMap from './CropMap'
import _cloneDeep from 'lodash/cloneDeep'
import _isEqual from 'lodash/isEqual'
import _get from 'lodash/get'
import computeDestinationPoint from 'geolib/es/computeDestinationPoint'
import { EQUIPMENT_TYPES_LABELS, DEFAULT_START_ANGLE } from '../../constants/const'
import { getLetterFromIndex, validateLatLon, parseStringToCoordinates, getBearing } from '../../lib/helpers'
import PivotCropArea from './PivotCropArea'

export default {
  components: {
    CropMap,
    PivotCropArea
  },
  data () {
    return {
      isFormValid: true,
      cropTypes: [],
      dateMenu: false,
      auxDate: '',
      cropMapSelection: true,
      rules: {
        fieldRequired: v => !!v || this.$t('Config.fieldIsRequired'),
        arrayFieldValidation: v => !!v && !!v.length || this.$t('Config.fieldIsRequired'),
        numberValidation: v => /^[+|-]?\d+([.]\d+)?$/.test(v) || this.$t('Config.onlyNumbers'),
        latitudeValidation: v => v <= 90 && v >= -90 || this.$t('Config.latitudeRule'),
        longitudeValidation: v => v <= 180 && v >= -180 || this.$t('Config.longitudeRule'),
        validateLatLon: v => validateLatLon(v) || this.$t('Config.allCoordinatesRule')
      },
      loading: false,
      newCropData: {
        shapeType: undefined
      },
      oldCropData: {
        shapeType: undefined
      },
      cropIRPoints: [],
      startAndEndPointsFlag: false,
      endPointEqualToStart: true,
      showCoordinates: false
    }
  },
  props: {
    cropData: {
      type: Object
    },
    comesFromAnotherRoute: {
      type: Boolean
    },
    comesFromAnotherRouteData: {
      type: Object
    },
    isNewCrop: {
      type: Boolean
    }
  },
  created () {
    if (!this.cropData.devices) {
      this.cropData.devices = []
    }
    this.fetchCropTypes()
    if (this.comesFromAnotherRoute) {
      this.cropIRPoints = this.comesFromAnotherRouteData.newCropData.points || []
      this.oldCropData = this.comesFromAnotherRouteData.oldCropData
      this.newCropData = this.comesFromAnotherRouteData.newCropData
      this.isNewCrop = this.comesFromAnotherRouteData.isNewCrop
    } else {
      if (typeof this.cropData.type === 'string') {
        this.cropData.type = this.cropData.type.split('|')
      }
      this.cropIRPoints = this.cropData.points || []
      this.oldCropData = {...this.oldCropData, ..._cloneDeep(this.cropData)}
      this.newCropData = {...this.newCropData, ..._cloneDeep(this.cropData)}
    }

    if (!_isEqual(this.newCropData.startPoint, this.newCropData.endPoint)) this.endPointEqualToStart = false
  },
  methods: {
    saveHandler () {
      setTimeout(async () => {
        if (!this.$refs.cropForm.validate()) {
          this.$root.snack.show({text: this.$t('Config.fillRequiredFields'), error: true})
          return
        }
        if (this.newCropData.shapeType && !this.validateCoordinates()) {
          this.$root.snack.show({text: this.$t('Config.coordsRequired'), error: true})
          return
        }

        if (this.newCropData.shapeType === 3) {
          this.newCropData.points = this.cropIRPoints
        }

        this.loading = true
        try {
          if (this.isNewCrop) {
            await createCrop(this.newCropData, this.$store.getters.activeCampaign)
          } else {
            await updateCrop(this.oldCropData, this.newCropData)
          }
          this.$root.snack.show({text: this.$t('UI.registerSuccess')})
          this.$router.push({name: 'ManageField', params: {field: this.$store.getters.fields.find(field => field.id === this.newCropData.field)}})
        } catch (error) {
          this.$root.snack.show({text: this.$t('UI.registerError'), error: true})
        } finally {
          this.loading = false
        }
      })
    },
    fetchCropTypes () {
      firestore.then(db => {
        db.collection('app/v1/constants').doc('crops').get().then(doc => {
          let newCropItems = doc.data().types.map(type => ({text: this.$t('CardDetails')[type] || type, value: type}))
          newCropItems.sort((a, b) => a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1)
          const notSetIndex = newCropItems.findIndex(crop => crop.value === 'notSpecified')
          if (notSetIndex !== -1) {
            newCropItems.push(newCropItems.splice(notSetIndex, 1)[0])
          }
          this.cropTypes = newCropItems
        }).catch(error => {
          this.snackBar = {
            show: true,
            text: error.message + '-' + error.code,
            color: 'error'
          }
        })
      })
    },
    toggleMap () {
      this.cropMapSelection = !this.cropMapSelection
    },
    toggleCoordinates () {
      this.showCoordinates = !this.showCoordinates
    },
    addNewField () {
      this.$router.push({name: 'AddField', params: {isNewField: true, comesFromAnotherRoute: true, comesFromAnotherRouteName: 'AddField', comesFromAnotherRouteData: {oldCropData: this.oldCropData, newCropData: this.newCropData, isNewCrop: this.isNewCrop}}})
    },
    getCoordinatesText (crop) {
      if (crop.location && crop.location.latitude) {
        const startPoint = crop.startPoint
        const endPoint = crop.endPoint
        const center = crop.location
        const radius = crop.radius ? `${crop.radius}` : '' // radius doesn't exist in pivot lots created before this form was submitted to production
        return `${center.latitude || ''},${center.longitude || ''};${radius};${startPoint.latitude || ''},${startPoint.longitude || ''};${endPoint.latitude || ''},${endPoint.longitude || ''}`
      } else if (crop.points && crop.points.length) {
        let coordinatesTxt = ''
        crop.points.forEach(point => {
          if (coordinatesTxt.length) {
            coordinatesTxt += `${point.latitude || ''},${point.longitude || ''}`
          } else {
            coordinatesTxt += `${point.latitude || ''},${point.longitude || ''}`
          }
        })
        return coordinatesTxt
      }
    },
    setCoordinatesData () {
      if (this.newCropData.shapeType === 1 && !this.newCropData.location) {
        this.$set(this.newCropData, 'location', { latitude: undefined, longitude: undefined })
        this.$set(this.newCropData, 'startPoint', { latitude: undefined, longitude: undefined })
        this.$set(this.newCropData, 'endPoint', { latitude: undefined, longitude: undefined })
        this.radius = ''
      } else if (this.newCropData.shapeType === 2) {
        this.$set(this.newCropData, 'startPoint', { latitude: undefined, longitude: undefined })
        this.$set(this.newCropData, 'endPoint', { latitude: undefined, longitude: undefined })
      } else if (this.newCropData.shapeType === 3 && !this.cropIRPoints.length) {
        this.cropIRPoints = [{}, {}, {}]
      }
    },
    addIRPoint () {
      this.cropIRPoints.push({})
    },
    deleteIRPoint (index) {
      if (this.cropIRPoints.length > 3) {
        this.cropIRPoints = this.cropIRPoints.filter((p, i) => i !== index)
      } else {
        this.$root.snack.show({text: this.$t('Config.IRCropWithLessThanThreePoints'), error: true})
      }
    },
    validateCoordinates () {
      if (this.newCropData.shapeType === 1) {
        const {location, startPoint, endPoint} = this.newCropData
        return [location, startPoint, endPoint].every(el => el && el.latitude && el.longitude)
      } else if (this.newCropData.shapeType === 2) {
        const {startPoint, endPoint} = this.newCropData
        return [startPoint, endPoint].every(el => el && el.latitude && el.longitude)
      } else if (this.newCropData.shapeType === 3) {
        return this.cropIRPoints && this.cropIRPoints.length >= 3 && this.cropIRPoints.every(el => el && el.latitude && el.longitude)
      }
    },
    getEquipmentTypeLabel (type) {
      return this.$t(`Config.${EQUIPMENT_TYPES_LABELS[type]}`)
    },
    handleNewCropDataChange () {
      if (this.isNewCrop || (this.newCropData.shapeType === 1 && !this.oldCropData.startCoordinatesString)) {
        const latitude = _get(this, 'newCropData.location.latitude')
        const longitude = _get(this, 'newCropData.location.longitude')
        const radius = _get(this, 'newCropData.radius')
        if (latitude && longitude && radius) {
          const start = computeDestinationPoint(
            { latitude, longitude },
            radius,
            DEFAULT_START_ANGLE
          )

          this.newCropData.startPoint.latitude = start.latitude
          this.newCropData.startPoint.longitude = start.longitude
          this.newCropData.startCoordinatesString = `${start.latitude}, ${start.longitude}`
          this.newCropData.startAngle = getBearing({latitude, longitude}, {latitude: start.latitude, longitude: start.longitude})

          if (this.endPointEqualToStart) {
            this.newCropData.endPoint.latitude = start.latitude
            this.newCropData.endPoint.longitude = start.longitude
            this.newCropData.endCoordinatesString = `${start.latitude}, ${start.longitude}`
            this.newCropData.endAngle = getBearing({latitude, longitude}, {latitude: start.latitude, longitude: start.longitude})
          }
        }
      } else if (this.newCropData.shapeType === 1) {
        if (this.newCropData.location && this.newCropData.location.latitude && this.newCropData.location.longitude && this.newCropData.startCoordinatesString) {
          this.newCropData.startAngle = getBearing(this.newCropData.location, this.newCropData.startPoint)
        }
        if (this.newCropData.location && this.newCropData.location.latitude && this.newCropData.location.longitude && this.newCropData.endCoordinatesString) {
          this.newCropData.endAngle = getBearing(this.newCropData.location, this.newCropData.endPoint)
        }
      }
    },
    getPointLetter (pointIndex) {
      return getLetterFromIndex(pointIndex + 1)
    },
    handleCenterCoordinatesBlur () {
      const coordinates = parseStringToCoordinates(this.newCropData.centerCoordinatesString)
      if (coordinates) {
        this.newCropData.location.latitude = coordinates.latitude
        this.newCropData.location.longitude = coordinates.longitude
        this.handleNewCropDataChange()
      }
    },
    handleCropCoordinatesBlur (coordinatesString, pointType) {
      const coordinates = parseStringToCoordinates(coordinatesString)
      if (coordinates) {
        this.newCropData[`${pointType}Point`].latitude = coordinates.latitude
        this.newCropData[`${pointType}Point`].longitude = coordinates.longitude

        if (this.newCropData.shapeType === 1) {
          this.newCropData[`${pointType}Angle`] = getBearing(this.newCropData.location, this.newCropData[`${pointType}Point`])

          if (pointType === 'start' && this.endPointEqualToStart) {
            this.newCropData.endCoordinatesString = this.newCropData.startCoordinatesString
            this.newCropData.endAngle = this.newCropData.startAngle
            this.newCropData.endPoint.latitude = this.newCropData.startPoint.latitude
            this.newCropData.endPoint.longitude = this.newCropData.startPoint.longitude
          }
        }
      }
    },
    handleIRCropCoordinatesBlur (coordinatesString, pointIndex) {
      const coordinates = parseStringToCoordinates(coordinatesString)
      if (coordinates) {
        this.cropIRPoints[pointIndex].latitude = coordinates.latitude
        this.cropIRPoints[pointIndex].longitude = coordinates.longitude
      }
    }
  },
  watch: {
    auxDate (val) {
      const [year, month, day] = val.split('-')
      const formattedDate = `${day}/${month}/${year}`
      this.newCropData.sowingDate = formattedDate
    },
    'newCropData.shapeType' (newVal, oldVal) {
      if (this.isNewCrop || (!this.isNewCrop && oldVal !== undefined)) this.setCoordinatesData()
    },
    'endPointEqualToStart' (newVal, oldVal) {
      if (newVal && !oldVal) {
        this.newCropData.endCoordinatesString = this.newCropData.startCoordinatesString
        this.newCropData.endAngle = this.newCropData.startAngle
        this.newCropData.endPoint.latitude = this.newCropData.startPoint.latitude
        this.newCropData.endPoint.longitude = this.newCropData.startPoint.longitude
      }
    },
    'newCropData.radius': 'handleNewCropDataChange',
    'newCropData.points' (val) {
      this.cropIRPoints = val && val.length ? val : []
    },
    'newCropData.devices': {
      handler (val) {
        if (val && val.length && !this.oldCropData.shapeType) {
          this.newCropData.shapeType = this.$store.getters.devices.find(device => device.id === val[0]).equipment.type
        } else if ((!val || !val.length) && !this.oldCropData.shapeType) {
          this.newCropData.shapeType = undefined
        }
      },
      deep: true
    }
  },
  computed: {
    fields () {
      return this.$store.getters.fields.sort((a, b) => a.name.localeCompare(b.name))
    },
    devices () {
      const devices = this.$store.getters.devices.sort((a, b) => a.campaign.entityName.localeCompare(b.campaign.entityName))
      if (this.newCropData.shapeType)
        return devices.filter(device => device.equipment.type === this.newCropData.shapeType)
      return devices
    }
  }
}
</script>

<style scoped>
.angle-hint {
  align-content: center;
  font-size: 0.875rem;
  color: #6F6F6F;
}
</style>



// WEBPACK FOOTER //
// CropEditor.vue?051f766c